// components
import SubFormLayout from 'components/layouts/SubFormLayout'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'

// utils
import { objectDetailsToDropdownFormatter } from 'utils/data-manipulation/common'
import {
  DISPATCH_TICKET_ENUM,
  DISPATCH_TICKET_TYPES_ARRAY,
  DT_STATUS_ENUM,
  RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS,
} from 'utils/constants'
import { addToDate } from 'utils/data-manipulation/common'
import { useFormContext } from 'react-hook-form'
import RecurringDispatchTicketModal from '../DispatchTicketContainer/RecurringDispatchTicketModal.tsx'
import { useState } from 'react'

const GeneralInfoForm = ({
  dispatchTicketData,
  ticketIsResolved,
  yardDetailsList,
  onTypeChange,
  onDeleteRecurringTickets,
  isLoading,
  setIntercompanyValue,
  ticketHasAssets,
}) => {
  const { inOrgMode } = useAppSelector(state => state.ui)
  const { watch, setValue } = useFormContext()
  const { settings } = useAppSelector(state => state.settings)
  const { enableIntercompanyDispatch } = useAppSelector(state => state.featureFlags.variables)
  const defaultSchedulingOffset = Number(settings?.dispatchSettings?.loadBoard?.defaultSchedulingOffset) || 0
  const defaultTripType = settings?.dispatchSettings?.loadBoard?.defaultTripType || null

  const [openRecurringDetails, setOpenRecurringDetails] = useState(false)

  const dispatchingYardLocationId = watch('dispatchingYardLocationId')
  const terminatingYardLocationId = watch('terminatingYardLocationId')
  const ticketStatus = watch('status')

  const isRequested = dispatchTicketData?.status === DT_STATUS_ENUM.REQUESTED

  const disableIntercompanyEdit =
    [DT_STATUS_ENUM.IN_PROGRESS, DT_STATUS_ENUM.COMPLETED, DT_STATUS_ENUM.FAILED].includes(ticketStatus) ||
    ticketHasAssets

  const { pickupAssets, dropoffAssets, pickAndReturnAssets, deliveryAssets, liveLoadAssets } = useAppSelector(
    state => state.dispatch,
  )

  const { enableMultiLocationYards } = useAppSelector(state => state.featureFlags?.variables || {})

  const ticketHasContainerAssets =
    (pickupAssets || []).length > 0 ||
    (dropoffAssets || []).length > 0 ||
    (pickAndReturnAssets || []).length > 0 ||
    (deliveryAssets || []).length > 0 ||
    (liveLoadAssets || []).length > 0

  const handleSaveCustomRecurringInfo = recurringRule => {
    setValue('recurringRule', recurringRule, { shouldDirty: true })
    setValue('recurringMode', RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.CUSTOM.id, { shouldDirty: true })
  }

  const handleRecurringChange = value => {
    if (value === RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.CUSTOM.id) {
      setOpenRecurringDetails(true)
    } else {
      setValue('recurringMode', value, { shouldDirty: true })

      const recurringRule = {
        frequency: value.toUpperCase(),
        includeDriver: true,
      }

      setValue('recurringRule', recurringRule, { shouldDirty: true })
    }
  }

  const formFields: any[] = [
    [
      {
        name: 'requestDate',
        label: 'Request Date',
        dataTest: 'request-date',
        fieldType: 'date',
        defaultDate: new Date(),
        InputLabelProps: { shrink: true },
        disabled: ticketIsResolved || isRequested,
        datePickerProps: { disabled: ticketIsResolved || isRequested },
      },
      {
        name: 'requestTime',
        label: 'Request Time',
        dataTest: 'request-time',
        fieldType: 'time',
        defaultTime: new Date(),
        disabled: ticketIsResolved || isRequested,
        timePickerProps: { disabled: ticketIsResolved || isRequested },
      },
    ],
    [
      {
        name: 'scheduleDate',
        label: 'Scheduled Date',
        dataTest: 'scheduled-date',
        fieldType: 'date',
        defaultDate: addToDate(new Date(), defaultSchedulingOffset),
        InputLabelProps: { shrink: true },
        disabled: ticketIsResolved || isRequested,
        datePickerProps: { disabled: ticketIsResolved || isRequested },
      },
      {
        name: 'scheduleTime',
        label: 'Scheduled Time',
        dataTest: 'scheduled-time',
        fieldType: 'time',
        defaultTime: null,
        disabled: ticketIsResolved || isRequested,
        timePickerProps: { disabled: ticketIsResolved || isRequested },
      },
    ],
  ]

  const bottomRowFormFields = [
    {
      name: 'type',
      label: 'Task Type',
      dataTest: 'type',
      fieldType: 'dropdown',
      selectProps: {
        onChange: ({ target: { value } }) => onTypeChange(value),
      },
      defaultValue: defaultTripType || DISPATCH_TICKET_ENUM.PICK_UP,
      dropdownOptions: DISPATCH_TICKET_TYPES_ARRAY,
      InputLabelProps: { shrink: true },
      disabled: ticketIsResolved || isRequested,
    },
    enableIntercompanyDispatch && {
      name: 'intercompany',
      label: 'Intercompany',
      dataTest: 'intercompany-checkbox',
      fieldType: 'checkbox',
      disabled: disableIntercompanyEdit,
      onChange: ({ target: { checked } }) => {
        setIntercompanyValue(checked)
      },
    },
  ]
  const startYardFormField = {
    name: 'dispatchingYardLocationId',
    label: 'Start Yard',
    dataTest: 'dispatching-yard-location-id',
    fieldType: 'dropdown',
    selectProps: {
      onChange: ({ target: { value } }) => {
        setValue('dispatchingYardLocationId', value, { shouldDirty: true })
        if (!terminatingYardLocationId) setValue('terminatingYardLocationId', value)
      },
    },
    dropdownOptions:
      yardDetailsList?.map((item, index) =>
        objectDetailsToDropdownFormatter(item, index, {
          idKey: item.defaultLocationId && enableMultiLocationYards ? 'defaultLocationId' : 'locationId',
        }),
      ) || [],
    disabled: ticketHasContainerAssets || ticketIsResolved || isRequested,
  }
  const endYardFormField = {
    name: 'terminatingYardLocationId',
    label: 'End Yard',
    dataTest: 'terminating-yard-location-id',
    fieldType: 'dropdown',
    selectProps: {
      onChange: ({ target: { value } }) => {
        setValue('terminatingYardLocationId', value, { shouldDirty: true })
        if (!dispatchingYardLocationId) setValue('dispatchingYardLocationId', value)
      },
    },
    dropdownOptions:
      yardDetailsList?.map((item, index) =>
        objectDetailsToDropdownFormatter(item, index, {
          idKey: item.defaultLocationId && enableMultiLocationYards ? 'defaultLocationId' : 'locationId',
        }),
      ) || [],
    disabled: ticketIsResolved || isRequested,
  }

  // add in start/end yard when in org mode
  if (inOrgMode) {
    const yardFormFields = [startYardFormField, endYardFormField]
    formFields.push([...bottomRowFormFields, ...yardFormFields])
  } else {
    formFields.push(bottomRowFormFields)
  }

  if (!ticketStatus && !isLoading && !isRequested) {
    formFields[1].push({
      name: 'recurringMode',
      label: 'Recurring Schedule',
      dataTest: 'recurring-details',
      fieldType: 'dropdown',
      disabled: ticketStatus || isRequested,
      defaultValue: RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.NONE.id,
      dropdownOptions: Object.values(RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS),
      selectProps: {
        onChange: ({ target: { value } }) => handleRecurringChange(value),
        onClick: event => {
          // If it's already custom, open the modal because onChange won't be triggered
          const value = event.target.dataset.value
          if (value === RECURRING_DISPATCH_TICKET_DROPDOWN_OPTIONS.CUSTOM.id) {
            handleRecurringChange(value)
          }
        },
      },
    })
  } else if (dispatchTicketData?.recurringDispatchTicket && !isRequested) {
    formFields[1].push({
      name: 'recurringDetailsButton',
      label: 'Recurring Details',
      dataTest: 'recurring-details-button',
      fieldType: 'button',
      onClick: () => setOpenRecurringDetails(true),
    })
  }

  return (
    <>
      <SubFormLayout formName="General Information" boxStyles={{ width: '50%', marginRight: 2 }} fields={formFields} />
      <RecurringDispatchTicketModal
        isOpen={openRecurringDetails}
        onSave={handleSaveCustomRecurringInfo}
        onDelete={onDeleteRecurringTickets}
        isEditable={!ticketStatus}
        onHideModal={() => setOpenRecurringDetails(false)}
        recurringData={dispatchTicketData?.recurringDispatchTicket}
      />
    </>
  )
}

export default GeneralInfoForm
